import { permittedFlag } from './activityFlagHelpers';

import { GRAMMAR } from '../utils/constants';
import { requestGet } from '../../../modules/request';

export interface ProofreaderConceptResult {
  metadata: {
    answer: string;
    correct: number;
    instructions: string;
    prompt: string;
    questionNumber: number;
    attemptNumber?: number;
    questionScore?: number;
    unchanged: boolean;
    question_concept_uid: string;
    question_uid?: string;
  };
  concept_uid: string;
  question_type: string;
}

export interface GrammarQuestion {
  uid?: string;
  concept_uid: string;
  prompt?: string;
  answers?: any;
  flag?: string;
}

export interface GrammarQuestionsForConcepts {
  [conceptUid: string]: GrammarQuestion[];
}

// Re-export permittedFlag with more descriptive name for this context
export const isGrammarQuestionFlagPermitted = permittedFlag;

export const extractIncorrectProofreaderConcepts = (proofreaderResults: ProofreaderConceptResult[]): string[] => {
  if (!proofreaderResults || proofreaderResults.length === 0) {
    return [];
  }

  return proofreaderResults.reduce((results: string[], cr) => {
    const { metadata, concept_uid } = cr;
    const { correct } = metadata;
    if (correct === 0 && !results.includes(concept_uid)) { 
      results.push(concept_uid);
    }
    return results;
  }, []);
};

export const determineGrammarQuestionQuantityForProofreader = (incorrectConceptCount: number): number => {
  /* per Curriculum standards for Proofreader follow-up:
    - 4 or less incorrect concepts: 3 grammar questions per concept
    - 5 to 9 incorrect concepts: 2 grammar questions per concept
    - 10 or more incorrect concepts: 1 grammar question per concept
  */
  if (incorrectConceptCount > 9) {
    return 1;
  } else if (incorrectConceptCount > 4) {
    return 2;
  }
  return 3;
};

export const fetchGrammarQuestionsAPI = (): Promise<{[key: string]: GrammarQuestion}> => {
  const questionApiUrl = `/api/v1/questions.json?question_type=${GRAMMAR}`;
  return requestGet(questionApiUrl, null, (error) => {
    // Error fetching Grammar questions for Proofreader follow-up
    throw error;
  });
};

export const filterGrammarQuestionsForProofreaderConcepts = (
  grammarQuestions: {[key: string]: GrammarQuestion}, 
  conceptUIDs: string[], 
  flag: string = 'production'
): GrammarQuestionsForConcepts => {
  const questionsForConcepts: GrammarQuestionsForConcepts = {};
  
  Object.keys(grammarQuestions).forEach(q => {
    const question = grammarQuestions[q];
    if (conceptUIDs.includes(question.concept_uid) && 
        question.prompt && 
        question.answers && 
        isGrammarQuestionFlagPermitted(flag, question.flag)) {
      question.uid = q;
      if (questionsForConcepts.hasOwnProperty(question.concept_uid)) {
        questionsForConcepts[question.concept_uid] = questionsForConcepts[question.concept_uid].concat(question);
      } else {
        questionsForConcepts[question.concept_uid] = [question];
      }
    }
  });
  
  return questionsForConcepts;
};

export const checkIfGrammarQuestionsExistForProofreaderSession = (proofreaderResults: ProofreaderConceptResult[]): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!proofreaderResults || proofreaderResults.length === 0) {
      resolve(false);
      return;
    }

    const incorrectConceptUIDs = extractIncorrectProofreaderConcepts(proofreaderResults);

    if (incorrectConceptUIDs.length === 0) {
      resolve(false);
      return;
    }

    fetchGrammarQuestionsAPI()
      .then((grammarQuestions) => {
        if (!grammarQuestions) {
          resolve(false);
          return;
        }

        const questionsForConcepts = filterGrammarQuestionsForProofreaderConcepts(grammarQuestions, incorrectConceptUIDs);
        const hasAvailableQuestions = Object.keys(questionsForConcepts).length > 0;
        resolve(hasAvailableQuestions);
      })
      .catch(() => {
        resolve(false);
      });
  });
};

export const buildGrammarConceptsFromProofreaderSession = (proofreaderResults: ProofreaderConceptResult[]) => {
  const incorrectConceptUIDs = extractIncorrectProofreaderConcepts(proofreaderResults);
  const quantity = determineGrammarQuestionQuantityForProofreader(incorrectConceptUIDs.length);
  
  const concepts: { [key: string]: { quantity: number } } = {};
  proofreaderResults.forEach(cr => {
    const { metadata, concept_uid } = cr;
    const { correct } = metadata;
    if (correct === 0) {
      concepts[concept_uid] = { quantity };
    }
  });
  
  return concepts;
};
